"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Activity, AlertTriangle, Archive, Bell, CalendarClock, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Clipboard, Copy, FileJson, Filter, Gauge, KeyRound, ListChecks, Loader2, Lock, MoreHorizontal, RefreshCw, Search, ShieldCheck, Square, SlidersHorizontal, Terminal, X, } from "lucide-react";
import { Component, createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, } from "react";
import { ENVIRONMENT_SEMANTICS, RISK_SEMANTICS, VERDICT_SEMANTICS, VERIFICATION_SEMANTICS, labelForState, railForState, } from "../state/semantics.js";
import { Slot } from "./slot.js";
import { assertRailPresent } from "../state/rail-guard.js";
const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
].join(",");
function getFocusableElements(container) {
    if (!container)
        return [];
    return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
        const style = window.getComputedStyle(element);
        return style.display !== "none" && style.visibility !== "hidden";
    });
}
export class ErrorBoundary extends Component {
    state = { error: null };
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        console.error("[HELM ErrorBoundary]", error, info.componentStack);
    }
    reset = () => this.setState({ error: null });
    render() {
        const { error } = this.state;
        if (!error)
            return this.props.children;
        if (this.props.fallback)
            return this.props.fallback(error, this.reset);
        return (_jsxs("div", { className: "error-boundary", role: "alert", "aria-live": "assertive", children: [_jsx("h2", { children: "Something failed in the console." }), _jsx("p", { className: "error-boundary-message", children: error.message || "Unknown error" }), _jsx("button", { type: "button", className: "helm-button helm-button--secondary helm-button--md", onClick: this.reset, children: "Reset" })] }));
    }
}
export function useReducedMotion() {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function")
            return false;
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function")
            return undefined;
        const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
        const handler = (event) => setReduced(event.matches);
        if (typeof mql.addEventListener === "function") {
            mql.addEventListener("change", handler);
            return () => mql.removeEventListener("change", handler);
        }
        mql.addListener(handler);
        return () => mql.removeListener(handler);
    }, []);
    return reduced;
}
function useDialogFocusTrap(open, dialogRef, onClose) {
    const previousFocusRef = useRef(null);
    useEffect(() => {
        if (!open)
            return undefined;
        previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const dialog = dialogRef.current;
        window.requestAnimationFrame(() => {
            const first = getFocusableElements(dialog)[0] ?? dialog;
            first?.focus();
        });
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
            }
            if (event.key !== "Tab" || !dialog)
                return;
            const focusable = getFocusableElements(dialog);
            if (!focusable.length) {
                event.preventDefault();
                dialog.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable.at(-1);
            if (!first || !last)
                return;
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            window.requestAnimationFrame(() => previousFocusRef.current?.focus());
        };
    }, [dialogRef, onClose, open]);
}
export function Button({ ref, variant = "secondary", size = "md", children, leading, trailing, disabled, type = "button", "aria-label": ariaLabel, onClick, asChild = false, }) {
    const className = `helm-button helm-button--${variant} helm-button--${size}`;
    if (asChild) {
        return (_jsx(Slot, { ref: ref, className: className, "aria-label": ariaLabel, "aria-disabled": disabled || undefined, "data-disabled": disabled || undefined, onClick: onClick, children: children }));
    }
    return (_jsxs("button", { ref: ref, type: type, className: className, disabled: disabled, "aria-label": ariaLabel, onClick: onClick, children: [leading ? _jsx("span", { className: "button-icon", children: leading }) : null, _jsx("span", { children: children }), trailing ? _jsx("span", { className: "button-icon", children: trailing }) : null] }));
}
export function BoundaryRail({ state, intensity = "active", className = "" }) {
    return _jsx("span", { "aria-hidden": "true", className: `boundary-rail rail--${state} rail-intensity--${intensity} ${className}`.trim() });
}
export function Badge({ state = "historical", label, tone = "neutral", intensity = "active", dot = false, asChild = false, children, }) {
    const rail = railForState(state);
    const className = `helm-badge badge-tone--${tone} rail-text--${rail} badge-intensity--${intensity}`;
    if (asChild) {
        return _jsx(Slot, { className: className, children: children });
    }
    return (_jsxs("span", { className: className, children: [dot ? _jsx("span", { className: `badge-dot rail-bg--${rail}`, "aria-hidden": "true" }) : null, label ?? labelForState(state)] }));
}
export function VerdictBadge({ state, intensity = "active" }) {
    const spec = VERDICT_SEMANTICS[state];
    return (_jsxs("span", { className: `verdict-badge rail-text--${spec.rail} badge-intensity--${intensity}`, "aria-label": `Verdict ${spec.label}. ${spec.description}`, children: [_jsx(BoundaryRail, { state: spec.rail, intensity: intensity }), spec.label] }));
}
export function VerificationStatus({ state, label }) {
    const spec = VERIFICATION_SEMANTICS[state];
    const Icon = state === "verified" || state === "exported" ? CheckCircle2 : state === "failed" ? X : state === "pending" ? Loader2 : Archive;
    return (_jsxs("span", { className: `verification-status rail-text--${spec.rail}`, "aria-label": `${spec.label}. ${spec.description}`, children: [_jsx(Icon, { size: 13, strokeWidth: 1.8, "aria-hidden": "true" }), label ?? spec.label] }));
}
export function EnvironmentBadge({ env }) {
    const spec = ENVIRONMENT_SEMANTICS[env];
    return _jsx(Badge, { state: env, label: spec.label, tone: "env", dot: true });
}
export function RiskBadge({ risk }) {
    const spec = RISK_SEMANTICS[risk];
    return _jsx(Badge, { state: risk, label: spec.label, tone: "risk" });
}
export function HashText({ value, label, kind = "hash", truncate = true }) {
    const [copied, setCopied] = useState(false);
    const visible = truncate && value.length > 22 ? `${value.slice(0, 12)}...${value.slice(-6)}` : value;
    const liveId = useId();
    const toast = useContext(ToastContext);
    const copy = async () => {
        try {
            if (!navigator.clipboard)
                throw new Error("Clipboard API unavailable in this browser context.");
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 900);
        }
        catch (error) {
            toast?.push({
                title: `Could not copy ${kind}`,
                detail: error instanceof Error ? error.message : "Browser blocked clipboard access.",
                tone: "failed",
            });
        }
    };
    return (_jsxs("span", { className: `hash-text hash-text--${kind}`, children: [label ? _jsx("span", { className: "meta-label", children: label }) : null, _jsx("code", { title: value, children: visible }), _jsx(Tooltip, { label: copied ? "Copied" : "Copy", children: _jsx("button", { className: "icon-button", type: "button", "aria-label": `Copy ${kind} ${value}`, "aria-describedby": liveId, onClick: copy, children: copied ? _jsx(Check, { size: 13, "aria-hidden": "true" }) : _jsx(Copy, { size: 13, "aria-hidden": "true" }) }) }), _jsx("span", { id: liveId, className: "sr-only", "aria-live": "polite", children: copied ? `${kind} copied` : "" })] }));
}
export function Panel({ title, kicker, actions, children, priority = "secondary", rail }) {
    if (priority === "primary" && !rail) {
        assertRailPresent("verified", false, `Panel("${title ?? kicker ?? "primary"}")`);
    }
    return (_jsxs("section", { className: `panel panel--${priority} ${rail ? `panel--rail rail-border--${rail}` : ""}`.trim(), children: [title || kicker || actions ? (_jsxs("header", { className: "panel-header", children: [_jsxs("div", { children: [kicker ? _jsx("div", { className: "kicker", children: kicker }) : null, title ? _jsx("h2", { children: title }) : null] }), actions ? _jsx("div", { className: "panel-actions", children: actions }) : null] })) : null, _jsx("div", { className: "panel-body", children: children })] }));
}
export function Tooltip({ label, children, asChild = false }) {
    const id = useId();
    return (_jsxs("span", { className: "tooltip-root", children: [asChild ? (_jsx(Slot, { "aria-describedby": id, children: children })) : (_jsx("span", { "aria-describedby": id, children: children })), _jsx("span", { id: id, role: "tooltip", className: "tooltip-content", children: label })] }));
}
export function Tabs({ value, options, onChange, label, variant = "inline" }) {
    const refs = useRef([]);
    const onKeyDown = (event) => {
        const activeIndex = Math.max(0, options.findIndex((option) => option.value === value));
        const last = options.length - 1;
        const nextIndex = event.key === "ArrowRight" ? Math.min(last, activeIndex + 1) :
            event.key === "ArrowLeft" ? Math.max(0, activeIndex - 1) :
                event.key === "Home" ? 0 :
                    event.key === "End" ? last :
                        activeIndex;
        if (nextIndex === activeIndex && event.key !== "Home" && event.key !== "End")
            return;
        if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key))
            return;
        event.preventDefault();
        const next = options[nextIndex];
        if (!next)
            return;
        onChange(next.value);
        window.requestAnimationFrame(() => refs.current[nextIndex]?.focus());
    };
    return (_jsx("div", { className: `tabs tabs--${variant}`, role: "tablist", "aria-label": label, onKeyDown: onKeyDown, children: options.map((option, index) => (_jsxs("button", { ref: (node) => { refs.current[index] = node; }, type: "button", role: "tab", "aria-selected": value === option.value, tabIndex: value === option.value ? 0 : -1, className: `tab-button ${value === option.value ? "is-active" : ""}`, onClick: () => onChange(option.value), children: [_jsx("span", { children: option.label }), option.badge ? _jsx("span", { className: "tab-badge", children: option.badge }) : null] }, option.value))) }));
}
export function Drawer({ open, title, children, onClose, size = "md" }) {
    const dialogRef = useRef(null);
    useDialogFocusTrap(open, dialogRef, onClose);
    useEffect(() => {
        if (!open)
            return undefined;
        const body = document.body;
        const previousOverflow = body.style.overflow;
        const previousPaddingRight = body.style.paddingRight;
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;
        body.style.overflow = "hidden";
        if (scrollbar > 0)
            body.style.paddingRight = `${scrollbar}px`;
        return () => {
            body.style.overflow = previousOverflow;
            body.style.paddingRight = previousPaddingRight;
        };
    }, [open]);
    if (!open)
        return null;
    return (_jsx("div", { className: "drawer-backdrop", role: "presentation", onMouseDown: onClose, children: _jsxs("div", { ref: dialogRef, className: `drawer drawer--${size}`, role: "dialog", "aria-modal": "true", "aria-label": title, tabIndex: -1, onMouseDown: (event) => event.stopPropagation(), children: [_jsxs("header", { className: "drawer-header", children: [_jsx("h2", { children: title }), _jsx("button", { className: "icon-button", type: "button", "aria-label": "Close drawer", onClick: onClose, children: _jsx(X, { size: 15, "aria-hidden": "true" }) })] }), _jsx("div", { className: "drawer-body", children: children })] }) }));
}
/**
 * Centered modal dialog. Focus-trapped, ESC-dismissible, restores focus to
 * the previously-focused element on close. Pair with the `Drawer` primitive
 * for side-anchored overlays.
 *
 * The backdrop scroll-locks the body and dismisses the dialog on click
 * unless `closeOnBackdrop={false}`. Title is wired via `aria-labelledby`;
 * if `description` is set, the body is given an `aria-describedby` binding.
 */
export function Dialog({ open, title, description, children, footer, onClose, size = "md", closeOnBackdrop = true, showCloseButton = true, role = "dialog", }) {
    const dialogRef = useRef(null);
    const titleId = useId();
    const descriptionId = useId();
    useDialogFocusTrap(open, dialogRef, onClose);
    useEffect(() => {
        if (!open)
            return undefined;
        const body = document.body;
        const previousOverflow = body.style.overflow;
        const previousPaddingRight = body.style.paddingRight;
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;
        body.style.overflow = "hidden";
        if (scrollbar > 0)
            body.style.paddingRight = `${scrollbar}px`;
        return () => {
            body.style.overflow = previousOverflow;
            body.style.paddingRight = previousPaddingRight;
        };
    }, [open]);
    if (!open)
        return null;
    return (_jsx("div", { className: "dialog-backdrop", role: "presentation", onMouseDown: closeOnBackdrop ? onClose : undefined, children: _jsxs("div", { ref: dialogRef, className: `dialog dialog--${size}`, role: role, "aria-modal": "true", "aria-labelledby": titleId, "aria-describedby": description ? descriptionId : undefined, tabIndex: -1, onMouseDown: (event) => event.stopPropagation(), children: [_jsxs("header", { className: "dialog-header", children: [_jsxs("div", { className: "dialog-header-copy", children: [_jsx("h2", { id: titleId, children: title }), description ? (_jsx("p", { id: descriptionId, className: "dialog-description", children: description })) : null] }), showCloseButton ? (_jsx("button", { className: "icon-button", type: "button", "aria-label": "Close", onClick: onClose, children: _jsx(X, { size: 15, "aria-hidden": "true" }) })) : null] }), children ? _jsx("div", { className: "dialog-body", children: children }) : null, footer ? _jsx("footer", { className: "dialog-footer", children: footer }) : null] }) }));
}
/**
 * Destructive-action confirmation dialog. Uses `role="alertdialog"` so
 * assistive tech announces the prompt assertively. Backdrop is non-dismissive
 * by default and the close button is suppressed — the only paths out are
 * the explicit confirm / cancel buttons.
 */
export function AlertDialog({ open, title, description, confirmLabel, cancelLabel = "Cancel", intent = "deny", onConfirm, onCancel, busy = false, }) {
    const confirmVariant = intent === "danger" || intent === "deny"
        ? "deny"
        : intent === "escalate"
            ? "escalate"
            : "primary";
    return (_jsx(Dialog, { open: open, title: title, description: description, onClose: onCancel, size: "sm", closeOnBackdrop: false, showCloseButton: false, role: "alertdialog", footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", size: "md", onClick: onCancel, disabled: busy, children: cancelLabel }), _jsx(Button, { variant: confirmVariant, size: "md", onClick: onConfirm, disabled: busy, children: confirmLabel })] }) }));
}
export function EmptyState({ title, body, action }) {
    return (_jsxs("div", { className: "empty-state", children: [_jsx(Square, { size: 18, "aria-hidden": "true" }), _jsxs("div", { children: [_jsx("h3", { children: title }), _jsx("p", { children: body })] }), action ? _jsx("div", { className: "empty-actions", children: action }) : null] }));
}
export function LoadingState({ label = 'Loading…' }) {
    return (_jsxs("div", { className: "empty-state", children: [_jsx(Loader2, { className: "animate-spin", size: 18, "aria-hidden": "true" }), _jsx("div", { children: _jsx("p", { children: label }) })] }));
}
export function ErrorState({ title, error, retry }) {
    const description = error instanceof Error ? error.message : 'The service did not return a usable response.';
    return (_jsxs("div", { className: "empty-state error", children: [_jsx(AlertTriangle, { size: 18, "aria-hidden": "true" }), _jsxs("div", { children: [_jsx("h3", { children: title }), _jsx("p", { children: description })] }), retry ? _jsx("div", { className: "empty-actions", children: _jsx(Button, { onClick: retry, children: "Retry" }) }) : null] }));
}
export function StatusRow({ state, label, detail, }) {
    const rail = railForState(state);
    const semanticLabel = labelForState(state);
    const rowLabel = label && label !== semanticLabel ? label : undefined;
    return (_jsxs("div", { className: `status-row rail-border--${rail}`, children: [_jsx(Badge, { state: state, label: semanticLabel, dot: true }), rowLabel || detail ? (_jsxs("span", { className: "status-copy", children: [rowLabel ? _jsx("span", { className: "status-label", children: rowLabel }) : null, detail ? _jsx("span", { className: "status-detail", children: detail }) : null] })) : null] }));
}
export function MetricTile({ label, value, detail, state = "historical", trend, }) {
    const rail = railForState(state);
    return (_jsxs("article", { className: `metric-tile rail-border--${rail}`, children: [_jsxs("div", { className: "metric-head", children: [_jsx("span", { children: label }), _jsx(Gauge, { size: 14, "aria-hidden": "true" })] }), _jsx("strong", { children: value }), _jsxs("div", { className: "metric-foot", children: [detail ? _jsx("span", { children: detail }) : null, trend ? _jsx(Badge, { state: state, label: trend }) : null] })] }));
}
export function FilterBar({ filters, }) {
    return (_jsxs("div", { className: "filter-bar", "aria-label": "Active filters", children: [_jsx(Filter, { size: 14, "aria-hidden": "true" }), filters.map((filter) => (_jsxs("button", { type: "button", className: "filter-chip", "aria-label": `Filter ${filter.label}: ${filter.value}`, children: [_jsx("span", { children: filter.label }), filter.state ? _jsx("span", { className: `filter-dot rail-bg--${railForState(filter.state)}`, "aria-hidden": "true" }) : null, _jsx("strong", { children: filter.value })] }, `${filter.label}-${filter.value}`))), _jsx("button", { type: "button", className: "icon-button", "aria-label": "Open advanced filters", children: _jsx(SlidersHorizontal, { size: 14, "aria-hidden": "true" }) })] }));
}
export function SegmentedControl({ value, options, onChange, label, }) {
    return (_jsx("div", { className: "segmented-control", role: "radiogroup", "aria-label": label, children: options.map((option) => (_jsx("button", { type: "button", role: "radio", "aria-checked": option.value === value, className: option.value === value ? "is-active" : "", onClick: () => onChange(option.value), children: option.label }, option.value))) }));
}
export function Stepper({ steps, }) {
    return (_jsx("ol", { className: "stepper", "aria-label": "Process steps", children: steps.map((step, index) => (_jsxs("li", { className: `rail-border--${railForState(step.state)}`, children: [_jsx("span", { className: "step-index", children: String(index + 1).padStart(2, "0") }), _jsxs("div", { children: [_jsx("strong", { children: step.label }), _jsx("span", { children: step.detail })] }), _jsx(Badge, { state: step.state, label: labelForState(step.state), dot: true })] }, step.label))) }));
}
export function ProgressRail({ label, value, state = "verified", }) {
    const rail = railForState(state);
    return (_jsxs("div", { className: "progress-rail", "aria-label": `${label}: ${value}%`, children: [_jsxs("div", { className: "progress-meta", children: [_jsx("span", { children: label }), _jsxs("strong", { children: [value, "%"] })] }), _jsx("div", { className: "progress-track", children: _jsx("span", { className: `progress-fill rail-bg--${rail}`, style: { width: `${value}%` } }) })] }));
}
export function SkeletonRows({ count = 4 }) {
    return (_jsx("div", { className: "skeleton-stack", role: "status", "aria-label": "Loading rows", children: Array.from({ length: count }, (_, index) => (_jsxs("div", { className: "skeleton-row", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] }, index))) }));
}
export function Pagination({ page, pages, }) {
    return (_jsxs("nav", { className: "pagination", "aria-label": "Pagination", children: [_jsx("button", { type: "button", className: "icon-button", "aria-label": "Previous page", disabled: page <= 1, children: _jsx(ChevronLeft, { size: 14, "aria-hidden": "true" }) }), _jsxs("span", { children: ["Page ", _jsx("strong", { children: page }), " of ", _jsx("strong", { children: pages })] }), _jsx("button", { type: "button", className: "icon-button", "aria-label": "Next page", disabled: page >= pages, children: _jsx(ChevronRight, { size: 14, "aria-hidden": "true" }) })] }));
}
const ToastContext = createContext(null);
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const timersRef = useRef(new Map());
    const seedRef = useRef(0);
    const dismiss = useCallback((id) => {
        const handle = timersRef.current.get(id);
        if (handle !== undefined) {
            window.clearTimeout(handle);
            timersRef.current.delete(id);
        }
        setToasts((items) => items.filter((toast) => toast.id !== id));
    }, []);
    const push = useCallback((input) => {
        seedRef.current += 1;
        const id = `toast-${seedRef.current}`;
        const tone = input.tone ?? "verified";
        const duration = input.duration ?? 4500;
        setToasts((items) => [...items, { ...input, id, tone }]);
        if (duration > 0 && Number.isFinite(duration)) {
            const handle = window.setTimeout(() => dismiss(id), duration);
            timersRef.current.set(id, handle);
        }
        return id;
    }, [dismiss]);
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            for (const handle of timers.values())
                window.clearTimeout(handle);
            timers.clear();
        };
    }, []);
    const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);
    return _jsx(ToastContext.Provider, { value: value, children: children });
}
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        throw new Error("useToast must be used inside <ToastProvider>");
    return { push: ctx.push, dismiss: ctx.dismiss };
}
export function useOptionalToast() {
    const ctx = useContext(ToastContext);
    return useMemo(() => ({
        push: ctx?.push ?? (() => ""),
        dismiss: ctx?.dismiss ?? (() => undefined),
    }), [ctx]);
}
const TOAST_ICON = {
    verified: CheckCircle2,
    escalate: Bell,
    live: Activity,
    deny: X,
    pending: Loader2,
    failed: AlertTriangle,
};
export function Toaster() {
    const ctx = useContext(ToastContext);
    if (!ctx)
        return null;
    return (_jsx("div", { className: "toast-viewport", role: "region", "aria-live": "polite", "aria-label": "Notifications", children: ctx.toasts.map((toast) => {
            const Icon = TOAST_ICON[toast.tone];
            return (_jsxs("article", { className: `toast rail-border--${toast.tone}`, children: [_jsx(Icon, { size: 14, "aria-hidden": true }), _jsxs("div", { className: "toast-body", children: [_jsx("strong", { children: toast.title }), toast.detail ? _jsx("span", { children: toast.detail }) : null] }), _jsxs("div", { className: "toast-controls", children: [toast.action ? (_jsx("button", { type: "button", className: "toast-action", onClick: () => {
                                    toast.action?.onClick();
                                    ctx.dismiss(toast.id);
                                }, children: toast.action.label })) : null, _jsx("button", { type: "button", className: "icon-button", "aria-label": "Dismiss notification", onClick: () => ctx.dismiss(toast.id), children: _jsx(X, { size: 13, "aria-hidden": "true" }) })] })] }, toast.id));
        }) }));
}
export function ToastStack() {
    return (_jsxs("div", { className: "toast-stack", "aria-label": "Notification examples", children: [_jsxs("article", { className: "toast rail-border--verified", children: [_jsx(CheckCircle2, { size: 14, "aria-hidden": "true" }), _jsxs("div", { children: [_jsx("strong", { children: "Receipt verified" }), _jsx("span", { children: "ep_9f82c31a \u00B7 manifest hash matched" })] })] }), _jsxs("article", { className: "toast rail-border--escalate", children: [_jsx(Bell, { size: 14, "aria-hidden": "true" }), _jsxs("div", { children: [_jsx("strong", { children: "Second reviewer required" }), _jsx("span", { children: "release.high_risk.v2 \u00B7 production" })] })] }), _jsxs("article", { className: "toast rail-border--live", children: [_jsx(Activity, { size: 14, "aria-hidden": "true" }), _jsxs("div", { children: [_jsx("strong", { children: "Live feed streaming" }), _jsx("span", { children: "6 decisions in the last minute" })] })] })] }));
}
export function CommandBar() {
    return (_jsxs("div", { className: "command-bar", "aria-label": "Object command bar", children: [_jsx(Button, { variant: "proof", size: "sm", leading: _jsx(ShieldCheck, { size: 13, "aria-hidden": "true" }), children: "Verify" }), _jsx(Button, { variant: "secondary", size: "sm", leading: _jsx(RefreshCw, { size: 13, "aria-hidden": "true" }), children: "Replay" }), _jsx(Button, { variant: "terminal", size: "sm", leading: _jsx(Terminal, { size: 13, "aria-hidden": "true" }), children: "CLI" }), _jsx("button", { type: "button", className: "icon-button", "aria-label": "More object actions", children: _jsx(MoreHorizontal, { size: 14, "aria-hidden": "true" }) })] }));
}
export function KeyValueList({ items, }) {
    return (_jsx("dl", { className: "key-value-list", children: items.map((item) => (_jsxs("div", { children: [_jsx("dt", { children: item.label }), _jsx("dd", { children: item.value })] }, item.label))) }));
}
export function TimelineScrubber() {
    return (_jsxs("div", { className: "timeline-scrubber", children: [_jsx(CalendarClock, { size: 14, "aria-hidden": "true" }), _jsxs("div", { className: "timeline-track", "aria-hidden": "true", children: [_jsx("span", { className: "timeline-range" }), _jsx("span", { className: "timeline-thumb" })] }), _jsx("span", { children: "19:15:50.089Z" })] }));
}
export function ChecklistPanel({ items, state = "verified", }) {
    return (_jsx("div", { className: "checklist-panel", children: items.map((item) => (_jsxs("div", { className: `checklist-row rail-border--${railForState(state)}`, children: [_jsx(ListChecks, { size: 14, "aria-hidden": "true" }), _jsx("span", { children: item })] }, item))) }));
}
export function ActionRecordRow({ record, density = "compact", mode = "shared", selected = false, permission = "allowed", onInspect }) {
    const rail = selected ? "selected" : VERDICT_SEMANTICS[record.verdict].rail;
    return (_jsxs("tr", { className: `action-row rail-border--${rail} density--${density} mode--${mode}`, "data-selected": selected || undefined, "data-permission": permission, children: [_jsx("td", { "data-label": "Timestamp", children: _jsx("code", { children: record.timestamp }) }), _jsx("td", { "data-label": "Agent", children: record.agent }), _jsx("td", { "data-label": "Action", children: record.action }), _jsx("td", { "data-label": "Target", children: record.target }), _jsx("td", { "data-label": "Environment", children: _jsx(EnvironmentBadge, { env: record.environment }) }), _jsx("td", { "data-label": "Risk", children: _jsx(RiskBadge, { risk: record.risk }) }), _jsx("td", { "data-label": "Policy", children: _jsx("code", { children: record.policy }) }), _jsx("td", { "data-label": "Verdict", children: _jsx(VerdictBadge, { state: record.verdict, intensity: selected ? "active" : "historical" }) }), _jsx("td", { "data-label": "Receipt", children: _jsx(HashText, { value: record.receipt, kind: "receipt", truncate: true }) }), _jsx("td", { "data-label": "Verification", children: _jsx(VerificationStatus, { state: record.verification }) }), _jsx("td", { "data-label": "Actions", children: _jsx(Button, { variant: "ghost", size: "sm", onClick: () => onInspect?.(record), children: "Inspect" }) })] }));
}
const ACTION_TABLE_INITIAL_WINDOW = 100;
const ACTION_TABLE_INCREMENT = 100;
export function ActionRecordTable({ records, selectedId, onInspect, }) {
    const total = records.length;
    const [windowSize, setWindowSize] = useState(ACTION_TABLE_INITIAL_WINDOW);
    const sentinelRef = useRef(null);
    const visibleWindowSize = Math.min(windowSize, total);
    useEffect(() => {
        if (visibleWindowSize >= total)
            return undefined;
        const sentinel = sentinelRef.current;
        if (!sentinel || typeof IntersectionObserver === "undefined")
            return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setWindowSize((current) => Math.min(total, current + ACTION_TABLE_INCREMENT));
            }
        }, { rootMargin: "240px" });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [total, visibleWindowSize]);
    const slice = useMemo(() => records.slice(0, visibleWindowSize), [records, visibleWindowSize]);
    const remaining = total - visibleWindowSize;
    return (_jsx("div", { className: "table-frame", tabIndex: 0, "aria-label": "Scrollable action records table", children: _jsxs("table", { className: "data-table action-table", children: [_jsx("caption", { className: "sr-only", children: "Action records in canonical timestamp, agent, action, target, environment, risk, policy, verdict, receipt, verification, actions order." }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Timestamp" }), _jsx("th", { children: "Agent" }), _jsx("th", { children: "Action" }), _jsx("th", { children: "Target" }), _jsx("th", { children: "Environment" }), _jsx("th", { children: "Risk" }), _jsx("th", { children: "Policy" }), _jsx("th", { children: "Verdict" }), _jsx("th", { children: "Receipt" }), _jsx("th", { children: "Verification" }), _jsx("th", { children: "Actions" })] }) }), _jsxs("tbody", { children: [slice.map((record) => (_jsx(ActionRecordRow, { record: record, selected: record.id === selectedId, onInspect: onInspect }, record.id))), remaining > 0 ? (_jsx("tr", { className: "action-table-sentinel", children: _jsxs("td", { ref: sentinelRef, colSpan: 11, "aria-live": "polite", children: [_jsxs("span", { children: ["Showing ", windowSize.toLocaleString(), " of ", total.toLocaleString(), " records."] }), _jsx("button", { type: "button", className: "link-button", onClick: () => setWindowSize(total), children: "Show all" })] }) })) : null] })] }) }));
}
export function CommandPalette({ open, items, onClose, onSelect, }) {
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const dialogRef = useRef(null);
    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle)
            return items;
        return items.filter((item) => item.label.toLowerCase().includes(needle) || item.kind.includes(needle));
    }, [items, query]);
    useDialogFocusTrap(open, dialogRef, onClose);
    useEffect(() => {
        if (!open)
            return;
        window.requestAnimationFrame(() => inputRef.current?.focus());
    }, [open]);
    if (!open)
        return null;
    const activeItem = filtered[active];
    return (_jsx("div", { className: "palette-backdrop", role: "presentation", onMouseDown: onClose, children: _jsxs("div", { ref: dialogRef, className: "command-palette", role: "dialog", "aria-modal": "true", "aria-label": "Command palette", tabIndex: -1, onMouseDown: (event) => event.stopPropagation(), children: [_jsxs("label", { className: "palette-search", children: [_jsx(Search, { size: 15, "aria-hidden": "true" }), _jsx("input", { ref: inputRef, value: query, placeholder: "Ask HELM or search receipts, policies, agents...", role: "combobox", "aria-expanded": "true", "aria-controls": "palette-results", "aria-activedescendant": activeItem ? `palette-${activeItem.id}` : undefined, onChange: (event) => {
                                setQuery(event.target.value);
                                setActive(0);
                            }, onKeyDown: (event) => {
                                if (event.key === "ArrowDown") {
                                    event.preventDefault();
                                    setActive((index) => Math.min(filtered.length - 1, index + 1));
                                }
                                if (event.key === "ArrowUp") {
                                    event.preventDefault();
                                    setActive((index) => Math.max(0, index - 1));
                                }
                                if (event.key === "Enter" && activeItem) {
                                    onSelect(activeItem);
                                    onClose();
                                }
                            } })] }), _jsxs("div", { id: "palette-results", className: "palette-results", role: "listbox", "aria-label": "Command results", children: [filtered.length === 0 ? _jsx("div", { className: "palette-empty", children: "No matching command or source." }) : null, filtered.map((item, index) => (_jsxs("button", { id: `palette-${item.id}`, type: "button", role: "option", "aria-selected": index === active, className: `palette-result ${index === active ? "is-active" : ""}`, onMouseEnter: () => setActive(index), onClick: () => {
                                onSelect(item);
                                onClose();
                            }, children: [_jsx("span", { className: "palette-kind", children: item.kind }), _jsx("span", { children: item.label }), item.shortcut ? _jsx("kbd", { children: item.shortcut }) : null] }, item.id)))] }), _jsx("footer", { className: "palette-footer", children: "Arrow keys navigate. Enter opens. Escape closes. Assistant answers must cite HELM sources." })] }) }));
}
export function ShellIcon({ name }) {
    const size = 15;
    switch (name) {
        case "shield": return _jsx(ShieldCheck, { size: size, "aria-hidden": "true" });
        case "terminal": return _jsx(Terminal, { size: size, "aria-hidden": "true" });
        case "receipt": return _jsx(Clipboard, { size: size, "aria-hidden": "true" });
        case "policy": return _jsx(KeyRound, { size: size, "aria-hidden": "true" });
        case "evidence": return _jsx(Archive, { size: size, "aria-hidden": "true" });
        case "approval": return _jsx(CheckCircle2, { size: size, "aria-hidden": "true" });
        case "lock": return _jsx(Lock, { size: size, "aria-hidden": "true" });
        case "json": return _jsx(FileJson, { size: size, "aria-hidden": "true" });
        case "chevron": return _jsx(ChevronDown, { size: size, "aria-hidden": "true" });
        case "warning": return _jsx(AlertTriangle, { size: size, "aria-hidden": "true" });
    }
}
